import React, { useState, useMemo, useCallback } from 'react';

// Memoized row for desktop
const TableRow = React.memo(({ row, columns, onRowClick }) => {
  return (
    <tr
      onClick={() => onRowClick?.(row)}
      className="hover:bg-primary-50/30 transition-colors cursor-pointer"
    >
      {columns.map((col, colIdx) => {
        const cellContent = col.renderCell
          ? col.renderCell({ row, value: row[col.field] })
          : col.valueGetter
          ? col.valueGetter({ row })
          : col.valueFormatter
          ? col.valueFormatter({ value: row[col.field], row })
          : row[col.field];

        return (
          <td key={colIdx} className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
            {cellContent}
          </td>
        );
      })}
    </tr>
  );
});

// Memoized row for mobile
const TableRowMobile = React.memo(({ row, columns, onRowClick }) => {
  return (
    <div
      className="p-4 bg-white rounded border flex flex-col gap-2 cursor-pointer hover:bg-primary-50/30"
      onClick={() => onRowClick?.(row)}
    >
      {columns.map((col, colIdx) => {
        const cellContent = col.renderCell
          ? col.renderCell({ row, value: row[col.field] })
          : col.valueGetter
          ? col.valueGetter({ row })
          : col.valueFormatter
          ? col.valueFormatter({ value: row[col.field], row })
          : row[col.field];

        return (
          <div key={colIdx} className="flex justify-between text-sm">
            <span className="font-semibold text-gray-500">{col.headerName}:</span>
            <span className="text-gray-900">{cellContent}</span>
          </div>
        );
      })}
    </div>
  );
});

export default function TableMarketing({ columns, data, loading, onRowClick }) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = useCallback(
    (col) => {
      if (col.sortable === false) return;
      if (sortField === col.field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(col.field);
        setSortDirection('asc');
      }
    },
    [sortField, sortDirection]
  );

  const sortedData = useMemo(() => {
    console.log(data);
    if (!sortField) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal === bVal) return 0;
      const comparison = aVal > bVal ? 1 : -1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortField, sortDirection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 sm:h-96">
        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 table-auto">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={`${idx}-${col.field}`}
                  onClick={() => handleSort(col)}
                  className={`px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider ${
                    col.sortable !== false ? 'cursor-pointer hover:bg-gray-100' : ''
                  }`}
                  style={{ width: col.width || undefined }}
                >
                  <div className="flex items-center gap-1">
                    {col.headerName}
                    {sortField === col.field && (
                      <span className="text-primary-700 text-sm">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500 text-sm">
                  Brak danych do wświetlenia
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => (
                <TableRow key={`${row.id}-${index}`} row={row} columns={columns} onRowClick={onRowClick} />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Stacked Table */}
      <div className="lg:hidden flex flex-col gap-3">
        {sortedData.length === 0 ? (
          <div className="p-4 bg-white rounded border border-gray-200 text-center text-gray-500">
            Brak danych
          </div>
        ) : (
          sortedData.map((row, index) => (
            <TableRowMobile key={`${row.id}-${index}`} row={row} columns={columns} onRowClick={onRowClick} />
          ))
        )}
      </div>
    </div>
  );
}
